import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { KeyUri } from "../key";
import moment from "moment";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";

const OrderHistory = ({ userid }) => {
  const navigate = useNavigate();
  const [orders, setOrders] = useState([]);
  const [filteredOrders, setFilteredOrders] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [searchTerm, setSearchTerm] = useState("");
  const [sortConfig, setSortConfig] = useState({
    key: "date",
    direction: "desc",
  });
  const [isLoading, setIsLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [selectedBooking, setSelectedBooking] = useState({
    route: null,
    date: new Date()
  });
  const [isSubmitting, setIsSubmitting] = useState(false);

  const ordersPerPage = 5;

  useEffect(() => {
    const fetchData = async () => {
      try {
        if (userid) {
          const response = await axios.get(
            `${KeyUri.BACKEND_URI}/get-bookings-user/${userid}`
          );
          const formattedOrders = response?.data?.map((order) => ({
            ...order,
            date: moment(order.date).format("YYYY-MM-DD"),
          })) || [];
          setOrders(formattedOrders);
          setFilteredOrders(formattedOrders);
        }
      } catch (error) {
        console.error("Error fetching data:", error);
      } finally {
        setIsLoading(false);
      }
    };
    fetchData();
  }, [userid]);

  useEffect(() => {
    const filtered = orders.filter((order) =>
      Object.values(order).some((value) =>
        value?.toString().toLowerCase().includes(searchTerm.toLowerCase())
      )
    );
    setFilteredOrders(filtered);
    setCurrentPage(1);
  }, [searchTerm, orders]);

  const sortOrders = (key) => {
    let direction = "asc";
    if (sortConfig.key === key && sortConfig.direction === "asc") {
      direction = "desc";
    }
    setSortConfig({ key, direction });

    const sorted = [...filteredOrders].sort((a, b) => {
      if (a[key] < b[key]) return direction === "asc" ? -1 : 1;
      if (a[key] > b[key]) return direction === "asc" ? 1 : -1;
      return 0;
    });
    setFilteredOrders(sorted);
  };

  // Pagination calculations
  const indexOfLastOrder = currentPage * ordersPerPage;
  const indexOfFirstOrder = indexOfLastOrder - ordersPerPage;
  const currentOrders = filteredOrders.slice(indexOfFirstOrder, indexOfLastOrder);
  const totalPages = Math.ceil(filteredOrders.length / ordersPerPage);

  const handleRowClick = (order) => {
    navigate('/detailedbooking', { state: { order: order } });
  };

  const handleDateSubmit = async () => {
    setIsSubmitting(true);
    try {
      const formattedDate = moment(selectedBooking.date).format('YYYY-MM-DD');
      const formData = {
        from: selectedBooking?.route?.route?.from_location?._id,
        to: selectedBooking?.route?.route?.to_location?._id,
        pickupStreet: selectedBooking?.route?.pickup_point,
        dropStreet: selectedBooking?.route?.drop_point,
        date: formattedDate,
        seats: selectedBooking?.route?.vehicleId?.category
      };
      localStorage.setItem('Selected Data', JSON.stringify(formData));
      navigate("/sharedcabs");
      setShowModal(false);
    } catch (error) {
      console.error("Error storing booking:", error);
      alert('Error saving details. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const date_in_normal_form = (rideDate) => {
    const rideDateObj = new Date(rideDate);
    const options = {
      weekday: "long",
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
    };
    return rideDateObj.toLocaleDateString("en-GB", options).replace(",", "");
  };

  return (
    <div className="min-h-[80vh] p-4 flex items-center justify-center mb-24" style={{ fontFamily: 'Lato, sans-serif' }}>
      {showModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-96">
            <h2 className="text-xl font-semibold mb-4">
              Select Date for {selectedBooking.route?.from_location?.name} to {selectedBooking.route?.to_location?.name}
            </h2>
            <DatePicker
              selected={selectedBooking.date}
              onChange={(date) => setSelectedBooking(prev => ({
                ...prev,
                date: date
              }))}
              minDate={new Date()}
              className="w-full p-2 border rounded mb-4"
            />
            <div className="flex justify-end gap-2">
              <button
                onClick={() => setShowModal(false)}
                className="px-4 py-2 text-gray-600 hover:bg-gray-100 rounded"
              >
                Cancel
              </button>
              <button
                onClick={handleDateSubmit}
                disabled={isSubmitting}
                className="px-4 py-2 bg-blue-500 text-white rounded hover:bg-blue-600 disabled:bg-blue-300"
              >
                {isSubmitting ? 'Searching...' : 'Search Cabs'}
              </button>
            </div>
          </div>
        </div>
      )}

      <div className="bg-white rounded-3xl shadow-2xl overflow-hidden w-full max-w-6xl">
        <div className="p-8">
          <h2 className="text-3xl font-bold text-gray-800 mb-6">
            Order History
          </h2>
          <div className="mb-4 flex justify-between items-center">
            <input
              type="text"
              placeholder="Search orders..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-10 pr-4 py-2 border-2 border-gray-200 rounded-lg focus:outline-none focus:border-teal-500 transition-colors"
            />
          </div>
          {isLoading ? (
            <div className="text-center py-10">
              <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-teal-500"></div>
              <p className="mt-2 text-gray-500">Loading orders...</p>
            </div>
          ) : (
            <>
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="bg-gray-50">
                      <th
                        onClick={() => sortOrders("from")}
                        className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer"
                      >
                        From
                      </th>
                      <th
                        onClick={() => sortOrders("to")}
                        className="px-8 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer"
                      >
                        To
                      </th>
                      <th
                        onClick={() => sortOrders("date")}
                        className="px-6 lg:px-1 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer"
                      >
                        Booked Date
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Ride Date
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Fare
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Status
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Actions
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {currentOrders.length > 0 ? (
                      currentOrders.map((order, index) => (
                        <tr 
                          key={index} 
                          className="border-b cursor-pointer hover:bg-gray-50 transition-colors"
                          onClick={() => handleRowClick(order)}
                        >
                          <td className="px-2 py-4 text-gray-700">
                            {order?.route?.from_location?.name}
                          </td>
                          <td className="px-5 py-4 text-gray-700">
                            {order?.route?.to_location?.name}
                          </td>
                          <td className="px-1 py-4 text-gray-700">{order?.date}</td>
                          <td className="px-6 py-4 text-gray-700">
                            {date_in_normal_form(order?.ride_date_and_time)}
                          </td>
                          <td className="px-5 py-4 text-gray-700">
                          ⟨₹⟩{order.payment_amount}
                          </td>
                          <td className={`px-6 py-4 font-semibold ${
                            order?.payment_status === "captured"
                              ? "bg-green-100 text-green-800"
                              : "bg-red-100 text-red-800"
                          } rounded-lg px-3`}>
                            {order?.payment_status === "captured" ? "Paid" : "Pending"}
                          </td>
                          <td className="px-6 py-4">
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                setSelectedBooking({
                                  route: order,
                                  date: new Date()
                                });
                                setShowModal(true);
                              }}
                              className="px-4 py-2 bg-blue-500 text-white rounded hover:bg-blue-600 transition-colors"
                            >
                              Repeat
                            </button>
                          </td>
                        </tr>
                      ))
                    ) : (
                      <tr>
                        <td colSpan="7" className="text-center py-6 text-gray-500">
                          No orders found
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
              
              {totalPages > 1 && (
              
                <div className="flex justify-center mt-4 items-center gap-2">
                  <button
                    onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                    disabled={currentPage === 1}
                    className="px-3 py-1 rounded-lg bg-gray-200 disabled:opacity-50 hover:bg-gray-300"
                  >
                    Previous
                  </button>


<div className="overflow-x-auto max-w-[60vw]">
    <div className="flex gap-1 w-max">
                  {Array.from({ length: totalPages }, (_, i) => (
                    <button
                      key={i + 1}
                      onClick={() => setCurrentPage(i + 1)}
                      className={`mx-1 px-3 py-1 rounded-lg text-sm ${
                        currentPage === i + 1
                          ? "bg-teal-500 text-white"
                          : "bg-gray-200 text-gray-800 hover:bg-gray-300"
                      }`}
                    >
                      {i + 1}
                    </button>
                  ))}
                  </div>
                  </div>

                  <button
                    onClick={() => setCurrentPage(p => 
                      Math.min(p + 1, totalPages)
                    )}
                    disabled={currentPage === totalPages}
                    className="px-3 py-1 rounded-lg bg-gray-200 disabled:opacity-50 hover:bg-gray-300"
                  >
                    Next
                  </button>
                </div>
            
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default OrderHistory;